import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import { addResizedColumns } from "./useTableConfig";
import { useCustomTableSchema } from "./useCustomTableSchema";
import { useDynamicColumns } from "./useDynamicColumns";
import { useDynamicFilters } from "./useDynamicFilters";
import TagService from "services/Tag";
import UserService from "services/User";
import {
  customTableName,
  customTableRowsKey,
} from "routes/custom_tables/grid/queryKeys";

export const useCustomTableGrid = (slug, options = {}) => {
  const { onDeleteRow } = options;
  const { schema, isFetched: isSchemaFetched } = useCustomTableSchema(slug);

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  // Read-role tags eligible for this table (per-slug + all_models), used to
  // build the Tags filter. `for_filtering` selects read-role tags server-side.
  // Leave ``data`` undefined until resolved so ``useDynamicFilters`` falls back
  // to its stable empty default (a fresh ``[]`` here would churn the memo).
  const { data: tagOptions } = useQuery({
    queryKey: ["custom-table-filter-tags", slug],
    queryFn: () =>
      TagService.getTags({ model: `CustomTable:${slug}`, for_filtering: true }),
    enabled: !!slug,
  });

  const isReady = isSchemaFetched && isTableSettingsFetched && !!schema;

  // Refetch the grid rows after an inline tag edit so the Tags cell refreshes.
  const onUpdate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: customTableRowsKey(slug) }),
    [slug]
  );

  const dynamicColumns = useDynamicColumns(isReady ? schema : null, {
    onDeleteRow,
    onUpdate,
  });

  const filters = useDynamicFilters(isReady ? schema : null, tagOptions);

  const columns = useMemo(() => {
    if (!isReady) return [];
    const settings = get(
      tableSettings,
      `user_settings.preferences.tables.${customTableName(slug)}`,
      {}
    );
    return addResizedColumns({
      columns: dynamicColumns,
      resizedColumns: settings?.resized_columns || {},
    });
  }, [isReady, dynamicColumns, tableSettings, slug]);

  return { columns, filters, isReady };
};
